<template>
  <div class="sdz">
    <label :for="inputId" class="sdz-label">
      <i v-if="full" class="el-icon-circle-check sdz-ok"></i>{{ label }}
    </label>
    <label
      v-if="!full"
      :for="inputId"
      class="sdz-zone"
      :class="{ 'sdz-zone--drag': drag }"
      @dragover.prevent="drag = true"
      @dragleave="drag = false"
      @drop.prevent="onDrop"
    >
      <i class="el-icon-upload2 sdz-icon"></i>
      <span class="sdz-title">{{ value.length ? "Arrastra el segundo archivo" : "Arrastra tu archivo aquí" }}</span>
      <span class="sdz-hint">o haz clic para elegirlo · {{ formats }} · máx. {{ maxMb }} MB</span>
      <span class="sdz-hint">{{ hint }}</span>
    </label>
    <input
      :id="inputId"
      type="file"
      class="sdz-input"
      :multiple="maxFiles > 1"
      :accept="exts.map(e => '.' + e).join(',')"
      @change="onPick"
    />
    <ul v-if="value.length" class="sdz-list">
      <li v-for="(f, i) in value" :key="f.name + i" class="sdz-item">
        <img v-if="previews[i]" :src="previews[i]" alt="" class="sdz-thumb" />
        <span v-else class="sdz-thumb sdz-thumb--doc"><i class="el-icon-document"></i></span>
        <span class="sdz-name">{{ f.name }}<small>{{ Math.round(f.size / 1024) }} KB</small></span>
        <button type="button" class="sdz-remove" :aria-label="'Quitar ' + f.name" @click="remove(i)">
          <i class="el-icon-close"></i>
        </button>
      </li>
    </ul>
    <p v-if="error" class="sdz-error">{{ error }}</p>
  </div>
</template>

<script>
const IMAGE_EXTS = ["jpg", "jpeg", "png", "webp", "heic"];
const ext = name => (name.split(".").pop() || "").toLowerCase();

/**
 * Zona de arrastrar y soltar para un documento. maxFiles = 1 reemplaza el
 * archivo al soltar otro; con 2 (cédula frontal y posterior) se acumulan.
 */
export default {
  name: "SignatureDropzone",
  props: {
    inputId: { type: String, required: true },
    label: { type: String, required: true },
    hint: { type: String, default: "" },
    exts: { type: Array, required: true },
    maxFiles: { type: Number, default: 1 },
    maxMb: { type: Number, default: 5 },
    value: { type: Array, default: () => [] }
  },
  data() {
    return { drag: false, error: null, previews: [] };
  },
  computed: {
    full() {
      return this.value.length >= this.maxFiles;
    },
    formats() {
      if (this.exts.every(e => IMAGE_EXTS.includes(e))) return "Imagen (JPG, PNG, WEBP o HEIC)";
      return this.exts.includes("jpg") ? "PDF o imagen" : "PDF";
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(files) {
        this.previews.forEach(u => u && URL.revokeObjectURL(u));
        this.previews = files.map(f => (["jpg", "jpeg", "png", "webp"].includes(ext(f.name)) ? URL.createObjectURL(f) : null));
      }
    }
  },
  beforeDestroy() {
    this.previews.forEach(u => u && URL.revokeObjectURL(u));
  },
  methods: {
    onDrop(e) {
      this.drag = false;
      this.add(e.dataTransfer.files);
    },
    onPick(e) {
      this.add(e.target.files);
      e.target.value = "";
    },
    add(list) {
      this.error = null;
      const picked = Array.from(list || []);
      if (!picked.length) return;
      const bad = picked.find(f => !this.exts.includes(ext(f.name)));
      if (bad) {
        this.error = `"${bad.name}" no es válido. Formato: ${this.formats}.`;
        return;
      }
      const big = picked.find(f => f.size > this.maxMb * 1024 * 1024);
      if (big) {
        this.error = `"${big.name}" pesa más de ${this.maxMb} MB.`;
        return;
      }
      if (this.maxFiles === 1) {
        this.$emit("input", [picked[0]]);
        return;
      }
      const next = [...this.value, ...picked];
      if (next.length > this.maxFiles) this.error = `Máximo ${this.maxFiles} archivos: se tomaron los primeros.`;
      this.$emit("input", next.slice(0, this.maxFiles));
    },
    remove(i) {
      this.$emit("input", this.value.filter((_, j) => j !== i));
    }
  }
};
</script>

<style lang="scss" scoped>
$primary: #31355c;
$accent: #3fdbbf;
$border: #e4eaf6;
$bg: #f7f8f9;

.sdz-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 700;
  color: $primary;
  margin-bottom: 6px;
}
.sdz-ok {
  color: #22a06b;
}
.sdz-zone {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 22px 14px;
  border: 2px dashed $border;
  border-radius: 12px;
  text-align: center;
  cursor: pointer;
  margin: 0;
  transition: all 0.15s;
  &:hover,
  &--drag {
    border-color: $accent;
    background: rgba($accent, 0.08);
  }
}
.sdz-icon {
  font-size: 22px;
  color: $accent;
}
.sdz-title {
  font-size: 13px;
  font-weight: 700;
  color: $primary;
}
.sdz-hint {
  font-size: 12px;
  color: #909399;
}
.sdz-input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}
.sdz-list {
  list-style: none;
  padding: 0;
  margin: 8px 0 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.sdz-item {
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid $border;
  border-radius: 10px;
  padding: 8px 10px;
}
.sdz-thumb {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  object-fit: cover;
  border: 1px solid $border;
  flex-shrink: 0;
  &--doc {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: $bg;
    color: #909399;
    font-size: 18px;
  }
}
.sdz-name {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  font-weight: 600;
  color: $primary;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  small {
    display: block;
    font-size: 11px;
    font-weight: 400;
    color: #909399;
  }
}
.sdz-remove {
  border: 0;
  background: transparent;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  color: #909399;
  &:hover {
    background: $bg;
    color: #e5484d;
  }
}
.sdz-error {
  margin: 6px 0 0;
  font-size: 12px;
  font-weight: 600;
  color: #e5484d;
}
</style>
